import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  Container,
  Grid,
  TextField,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Search } from "@mui/icons-material";
import { connect } from "react-redux";
import axios from "axios";
import config from "../../../config";
import * as Actions from "../../../redux/actions";
import { motion } from "framer-motion";
import PetsIcon from "@mui/icons-material/Pets";

const Varieties = ({ breedsRaw, classes, subBreedsRaw, loadBreeds, loadCategories, loadSubBreeds }) => {
  const { categoryId, breedId, subBreedId } = useParams();
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [varieties, setVarieties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [redirectLoader, setRedirectLoader] = useState(false);

  useEffect(() => {
    if (classes.length < 1) loadCategories();
    if (breedsRaw.length < 1) loadBreeds(categoryId);
    if (subBreedsRaw.length < 1) loadSubBreeds(breedId);

    const fetchVarieties = async () => {
      try {
        const res = await axios.get(
          `${config.SERVER_URL}/api/v1/sub_breeds/${subBreedId}/varieties`,
          { headers: { "Content-Type": "application/json" } }
        );
        setVarieties(res.status === 200 ? res.data.data : []);
      } catch (err) {
        setVarieties([]);
      }
      setLoading(false);
    };
    fetchVarieties();
  }, []);

  const category = classes.find((cat) => cat.id === parseInt(categoryId));
  const breed = breedsRaw.find((b) => b.id === parseInt(breedId));
  const subBreed = subBreedsRaw.find((s) => s.id === parseInt(subBreedId));

  useEffect(() => {
    if (category && !category.active) navigate("/classes");
  }, [category]);

  const handleGoNow = async (id) => {
    setRedirectLoader(true);
    try {
      const res = await axios.get(
        `${config.SERVER_URL}/api/v1/varieties/${id}/genetics`,
        { headers: { "Content-Type": "application/json" } }
      );
      if (res.status === 200 && res.data.data.length > 0) {
        navigate(`/classes/${categoryId}/breeds/${breedId}/subbreed/${subBreedId}/variety/${id}/genetics`);
        return;
      }
    } catch (err) {
      // fall through to listings
    }
    navigate(`/listings/variety/${id}`);
  };

  const filtered = varieties.filter(
    (item) => !search || item.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#111" }}>
      {/* Hero Header */}
      <Box
        sx={{
          position: "relative",
          background: "linear-gradient(to bottom, #1a1a1a 0%, #111 100%)",
          borderBottom: "1px solid #222",
          py: { xs: 5, md: 7 },
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: "radial-gradient(ellipse at 50% 0%, rgba(255,192,1,0.07) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
          {/* Breadcrumb */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3, flexWrap: "wrap" }}>
            {[
              { label: "Home", to: "/" },
              { label: "Classes", to: "/classes" },
              { label: category?.name, to: `/classes/${categoryId}/breeds` },
              { label: breed?.name, to: `/classes/${categoryId}/breeds/${breedId}/subbreeds` },
              { label: subBreed?.name, active: true },
            ]
              .filter(Boolean)
              .map((crumb, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <Typography sx={{ color: "#555", fontSize: "13px" }}>/</Typography>}
                  {crumb.active ? (
                    <Typography sx={{ color: "#FFC001", fontSize: "13px", fontWeight: 600 }}>
                      {crumb.label}
                    </Typography>
                  ) : (
                    <Typography
                      component={Link}
                      to={crumb.to}
                      sx={{ color: "#777", fontSize: "13px", textDecoration: "none", "&:hover": { color: "#FFC001" } }}
                    >
                      {crumb.label}
                    </Typography>
                  )}
                </React.Fragment>
              ))}
          </Box>

          {/* Title */}
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: "28px", sm: "36px", md: "48px" },
                color: "#fff",
                lineHeight: 1.1,
                mb: 1,
              }}
            >
              {subBreed?.name}{" "}
              <Box component="span" sx={{ color: "#FFC001" }}>
                Varieties
              </Box>
            </Typography>
            <Typography sx={{ color: "#777", fontSize: { xs: "13px", md: "15px" } }}>
              Select a variety to browse listings
            </Typography>
          </Box>

          {/* Search */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2, flexWrap: "wrap" }}>
            <TextField
              placeholder="Search varieties..."
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{
                width: { xs: "100%", sm: "420px" },
                backgroundColor: "#1a1a1a",
                borderRadius: "10px",
                "& .MuiOutlinedInput-root": {
                  paddingRight: 0,
                  color: "#fff",
                  borderRadius: "10px",
                  "& fieldset": { borderColor: "#333" },
                  "&:hover fieldset": { borderColor: "#FFC001" },
                  "&.Mui-focused fieldset": { borderColor: "#FFC001" },
                },
                "& .MuiInputBase-input::placeholder": { color: "#666", opacity: 1 },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Box
                      sx={{
                        backgroundColor: "#FFC001",
                        height: "40px",
                        width: "44px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderTopRightRadius: "10px",
                        borderBottomRightRadius: "10px",
                      }}
                    >
                      <Search sx={{ color: "#000", fontSize: 20 }} />
                    </Box>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {!loading && !redirectLoader && (
            <Box sx={{ textAlign: "center", mt: 2 }}>
              <Typography sx={{ color: "#555", fontSize: "13px" }}>
                {filtered.length} result{filtered.length !== 1 ? "s" : ""}{search ? ` for "${search}"` : ""}
              </Typography>
            </Box>
          )}
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="xl" sx={{ py: { xs: 4, md: 6 } }}>
        {loading || redirectLoader ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
            <CircularProgress sx={{ color: "#FFC001" }} />
          </Box>
        ) : filtered.length > 0 ? (
          <>
            <Grid container spacing={2.5}>
              {filtered.map((item, index) => (
                <Grid item xs={6} sm={4} md={3} lg={2.4} key={item.id}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.04 }}
                  >
                    <Box
                      onClick={() => handleGoNow(item.id)}
                      sx={{
                        cursor: "pointer",
                        borderRadius: "16px",
                        overflow: "hidden",
                        position: "relative",
                        height: { xs: "150px", sm: "180px", md: "200px" },
                        border: "1px solid #2a2a2a",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          borderColor: "#FFC001",
                          transform: "translateY(-5px)",
                          boxShadow: "0 12px 30px rgba(255,192,1,0.15)",
                          "& .card-bg": { filter: "blur(5px) brightness(0.3)", transform: "scale(1.1)" },
                          "& .card-label": { bottom: "14px" },
                          "& .card-badge": { opacity: 1, transform: "translateX(-50%) translateY(0)" },
                        },
                      }}
                    >
                      <Box className="card-bg" sx={{ position: "absolute", inset: 0, backgroundImage: item.image ? `url(${item.image})` : "none", backgroundColor: item.image ? "transparent" : "#1a1a1a", backgroundSize: "cover", backgroundPosition: "center", filter: "blur(0px) brightness(0.55)", transform: "scale(1.0)", transition: "all 0.4s ease" }} />
                      <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.2) 55%, transparent 100%)" }} />
                      <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, height: "3px", backgroundColor: "#FFC001", opacity: 0.9 }} />
                      {!item.image && (
                        <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <PetsIcon sx={{ fontSize: 40, color: "#FFC001", opacity: 0.4 }} />
                        </Box>
                      )}
                      {item.brand_promotions?.length > 0 && (
                        <Box component="img" src={item.brand_promotions[0].image} alt={item.brand_promotions[0].name} sx={{ position: "absolute", top: 10, right: 10, width: 36, height: 36, borderRadius: "50%", border: "2px solid #FFC001", objectFit: "contain", backgroundColor: "#000" }} />
                      )}
                      <Box className="card-label" sx={{ position: "absolute", bottom: "10px", left: 0, right: 0, px: 1.5, transition: "bottom 0.3s ease" }}>
                        <Typography noWrap sx={{ color: "#fff", fontWeight: 700, fontSize: { xs: "13px", sm: "14px", md: "15px" }, textShadow: "0 1px 6px rgba(0,0,0,0.9)" }}>
                          {item.name}
                        </Typography>
                      </Box>
                      <Box className="card-badge" sx={{ position: "absolute", bottom: "36px", left: "50%", transform: "translateX(-50%) translateY(8px)", opacity: 0, transition: "all 0.3s ease", backgroundColor: "#FFC001", color: "#000", fontWeight: 700, fontSize: "11px", px: 1.5, py: 0.4, borderRadius: "20px", whiteSpace: "nowrap" }}>
                        Explore →
                      </Box>
                    </Box>
                  </motion.div>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ textAlign: "center", mt: 5 }}>
              <Box
                component={Link}
                to={`/listings/subbreed/${subBreedId}`}
                sx={{ display: "inline-flex", alignItems: "center", gap: 1, backgroundColor: "#FFC001", color: "#000", fontWeight: 700, fontSize: { xs: "14px", md: "16px" }, px: 4, py: 1.5, borderRadius: "12px", textDecoration: "none", transition: "all 0.25s ease", "&:hover": { backgroundColor: "#e6ac00", transform: "translateY(-2px)" } }}
              >
                <PetsIcon sx={{ fontSize: 20 }} />
                See All {subBreed?.name} Listings
              </Box>
            </Box>
          </>
        ) : (
          <Box sx={{ textAlign: "center", py: 10 }}>
            <PetsIcon sx={{ fontSize: 60, color: "#333", mb: 2 }} />
            <Typography sx={{ color: "#555", fontSize: "16px" }}>
              No varieties found{search ? ` for "${search}"` : ""}.
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};

const mapStateToProps = ({ Categories }) => ({
  breedsRaw: Categories.breeds,
  classes: Categories.classes,
  subBreedsRaw: Categories.subBreeds,
});

const mapDispatchToProps = (dispatch) => ({
  loadBreeds: (categoryId) => dispatch(Actions.thunkLoadBreeds(categoryId)),
  loadCategories: () => dispatch(Actions.thunkLoadCategories()),
  loadSubBreeds: (breedId) => dispatch(Actions.thunkLoadSubBreeds(breedId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Varieties);
